import React, { useCallback, useEffect, useState } from "react";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  updatePassword,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

export default function ProfileSettings({ userData, handleLogout }) {
  const roleNumber = Number(userData?.role || 3);
  const accountTypeLabel =
    roleNumber === 1 ? "Staff" : roleNumber === 2 ? "Business" : "Individual";

  const [firstName, setFirstName] = useState(userData?.firstName || "");
  const [lastName, setLastName] = useState(userData?.lastName || "");
  const [organization, setOrganization] = useState(
    userData?.organization || userData?.freshbooksClientId?.organization || ""
  );

  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");

  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [phoneInput, setPhoneInput] = useState(
    userData?.phone || userData?.phoneNumber || ""
  );

  const resetPasswordState = useCallback(() => {
    setShowPasswordChange(false);
    setSavingPassword(false);
    setPasswordError("");
    setPasswordSuccess("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  }, []);

  useEffect(() => {
    setFirstName(userData?.firstName || "");
    setLastName(userData?.lastName || "");
    setOrganization(
      userData?.organization || userData?.freshbooksClientId?.organization || ""
    );
    setPhoneInput(userData?.phone || userData?.phoneNumber || "");
    resetPasswordState();
  }, [userData]);

  function formatPhoneNumber(phone) {
    if (!phone) return "N/A";
    const cleaned = ("" + phone).replace(/\D/g, "");
    if (cleaned.length === 10) {
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `(${match[1]}) ${match[2]}-${match[3]}`;
      }
    }
    return phone;
  }

  const toDateSafe = (ts) => {
    if (!ts) return null;
    if (ts instanceof Date) return ts;
    if (typeof ts?.toDate === "function") {
      try {
        return ts.toDate();
      } catch (_) {
        /* ignore */
      }
    }
    if (
      typeof ts === "object" &&
      ts !== null &&
      typeof ts.seconds === "number"
    ) {
      return new Date(ts.seconds * 1000);
    }
    if (typeof ts === "number") {
      const ms = ts < 1e12 ? ts * 1000 : ts;
      return new Date(ms);
    }
    if (typeof ts === "string") return new Date(ts);
    return null;
  };

  const memberSinceDate =
    toDateSafe(userData?.createdAt) ||
    toDateSafe(getAuth()?.currentUser?.metadata?.creationTime);

  const memberSinceLabel = memberSinceDate
    ? memberSinceDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

  const authEmail = getAuth()?.currentUser?.email || "—";

  function handlePasswordReset() {
    const auth = getAuth();
    const email = auth?.currentUser?.email || userData?.email;
    if (!email) {
      alert("No sign-in email found for this account.");
      return;
    }
    sendPasswordResetEmail(auth, email)
      .then(() => {
        alert("Password reset email sent.");
      })
      .catch((error) => {
        alert("Error sending password reset email: " + error.message);
      });
  }

  async function handlePasswordChangeSave() {
    setPasswordError("");
    setPasswordSuccess("");

    const auth = getAuth();
    const user = auth?.currentUser;
    const email = user?.email || userData?.email;

    const current = String(currentPassword || "");
    const next = String(newPassword || "");
    const confirm = String(confirmNewPassword || "");

    if (!user) {
      setPasswordError("You must be signed in to change your password.");
      return;
    }

    if (!email) {
      setPasswordError("No sign-in email found for this account.");
      return;
    }

    const supportsPassword =
      Array.isArray(user?.providerData) &&
      user.providerData.some((p) => p?.providerId === "password");
    if (!supportsPassword) {
      setPasswordError(
        "Password changes aren’t available for this sign-in method. Use a reset email instead."
      );
      return;
    }

    if (!current) {
      setPasswordError("Enter your current password.");
      return;
    }
    if (!next) {
      setPasswordError("Enter a new password.");
      return;
    }
    if (next !== confirm) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    if (next === current) {
      setPasswordError(
        "New password must be different from your current password."
      );
      return;
    }

    setSavingPassword(true);
    try {
      const credential = EmailAuthProvider.credential(email, current);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, next);
      setPasswordSuccess("Password updated.");
      setShowPasswordChange(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      console.error("Failed to update password", err);
      const code = err?.code || "";
      const message =
        code === "auth/wrong-password"
          ? "Current password is incorrect."
          : code === "auth/weak-password"
            ? "New password is too weak."
            : code === "auth/too-many-requests"
              ? "Too many attempts. Try again later."
              : err?.message || "Unable to update password right now.";
      setPasswordError(message);
    } finally {
      setSavingPassword(false);
    }
  }

  async function handleProfileSave() {
    if (!userData?.uid) {
      window.alert("Could not update profile (missing user id).");
      return;
    }

    const nextFirst = (firstName || "").trim();
    const nextLast = (lastName || "").trim();
    const nextOrg = (organization || "").trim();
    const nextPhone = (phoneInput || "").trim();

    const currentFirst = String(userData?.firstName || "").trim();
    const currentLast = String(userData?.lastName || "").trim();
    const currentOrg = String(userData?.organization || "").trim();
    const currentPhone = String(
      userData?.phone || userData?.phoneNumber || ""
    ).trim();

    const hasChanges =
      nextFirst !== currentFirst ||
      nextLast !== currentLast ||
      nextPhone !== currentPhone ||
      (roleNumber === 2 && nextOrg !== currentOrg);

    if (!hasChanges) {
      setProfileError("No changes to save.");
      return;
    }

    setSavingProfile(true);
    setProfileError("");
    try {
      const callable = httpsCallable(functions, "updateAccountDetails");
      await callable({
        firstName: nextFirst,
        lastName: nextLast,
        phone: nextPhone,
        ...(roleNumber === 2 ? { organization: nextOrg } : {}),
      });
      setIsEditingProfile(false);
      resetPasswordState();
      window.alert("Profile updated.");
    } catch (err) {
      console.error("Failed to update profile", err);
      setProfileError(err?.message || "Unable to update profile right now.");
    } finally {
      setSavingProfile(false);
    }
  }

  function handleProfileCancel() {
    setFirstName(userData?.firstName || "");
    setLastName(userData?.lastName || "");
    setOrganization(
      userData?.organization || userData?.freshbooksClientId?.organization || ""
    );
    setPhoneInput(userData?.phone || userData?.phoneNumber || "");
    setProfileError("");
    resetPasswordState();
    setIsEditingProfile(false);
  }

  const displayName =
    `${(firstName || "").trim()} ${(lastName || "").trim()}`.trim() ||
    userData?.email ||
    "Not provided";

  return (
    <div className="cp-settings">
      <div className="cp-settings__header">
        <div className="cp-orders__toolbarTitle">
          <div className="cp-orders__sectionTitle">Account details</div>
          <div className="cp-orders__sectionSub">
            We use this information for order updates and account communication.
          </div>
        </div>
        <div className="cp-settings__headerActions">
          {isEditingProfile ? (
            <>
              <button
                type="button"
                className="op-btn"
                onClick={handleProfileCancel}
                disabled={savingProfile}
              >
                Cancel
              </button>
              <button
                type="button"
                className="op-btn cp-cta--primary"
                onClick={handleProfileSave}
                disabled={savingProfile}
              >
                {savingProfile ? "Saving…" : "Save changes"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="op-btn cp-cta--secondary"
              onClick={() => {
                setIsEditingProfile(true);
                setProfileError("");
                resetPasswordState();
              }}
            >
              Edit profile
            </button>
          )}
        </div>
      </div>

      {profileError && (
        <div className="cp-settings__row">
          <div className="cp-settings__label" />
          <div className="cp-settings__value">
            <span className="cp-settings__hint cp-settings__hint--error">
              {profileError}
            </span>
          </div>
        </div>
      )}

      <div className="cp-settings__section cp-grid">
        <div className="cp-settings__row">
          <div className="cp-settings__label">Name</div>
          <div className="cp-settings__value">
            {isEditingProfile ? (
              <div className="cp-settings__editGrid">
                <input
                  className="cp-settings__editInput"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <input
                  className="cp-settings__editInput"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            ) : (
              <span>{displayName}</span>
            )}
          </div>
        </div>

        {roleNumber === 2 ? (
          <div className="cp-settings__row">
            <span className="cp-settings__label">Company</span>
            <div className="cp-settings__value">
              {isEditingProfile ? (
                <div className="cp-settings__editGrid">
                  <input
                    className="cp-settings__editInput"
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              ) : (
                <span className="cp-settings__value">{organization}</span>
              )}
            </div>
          </div>
        ) : null}
        <div className="cp-settings__row">
          <div className="cp-settings__label">Phone</div>
          <div className="cp-settings__value">
            {isEditingProfile ? (
              <div className="cp-settings__editGrid">
                <input
                  type="tel"
                  className="cp-settings__editInput"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  placeholder="Add a phone number"
                />
              </div>
            ) : (
              <span>
                {phoneInput ? formatPhoneNumber(phoneInput) : "Not provided"}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="cp-settings__section cp-grid">
        <div className="cp-settings__row">
          <div className="cp-settings__label">Email</div>
          <div className="cp-settings__value">{authEmail}</div>
        </div>
        <div className="cp-settings__row">
          <div className="cp-settings__label">Password</div>
          <div className="cp-settings__value">
            {isEditingProfile ? (
              <>
                {!showPasswordChange ? (
                  <>
                    {/* <button
                      type="button"
                      className="op-btn cp-cta--secondary"
                      onClick={() => {
                        setPasswordError("");
                        setPasswordSuccess("");
                        setShowPasswordChange(true);
                      }}
                      disabled={savingPassword}
                    >
                      Change password
                    </button> */}
                    <button
                      type="button"
                      className="op-btn"
                      onClick={handlePasswordReset}
                      disabled={savingPassword}
                      style={{ marginLeft: 8 }}
                    >
                      Send reset email
                    </button>
                    {passwordSuccess ? (
                      <span className="cp-settings__hint">
                        {passwordSuccess}
                      </span>
                    ) : null}
                    {passwordError ? (
                      <span className="cp-settings__hint cp-settings__hint--error">
                        {passwordError}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    <div className="cp-settings__editGrid">
                      <input
                        type="password"
                        className="cp-settings__editInput cp-settings__editInput--full"
                        placeholder="Current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        autoComplete="current-password"
                      />
                      <input
                        type="password"
                        className="cp-settings__editInput cp-settings__editInput--full"
                        placeholder="New password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <input
                        type="password"
                        className="cp-settings__editInput cp-settings__editInput--full"
                        placeholder="Confirm new password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => {
                          resetPasswordState();
                        }}
                        disabled={savingPassword}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="op-btn cp-cta--primary"
                        onClick={handlePasswordChangeSave}
                        disabled={savingPassword}
                        style={{ marginLeft: 8 }}
                      >
                        {savingPassword ? "Updating…" : "Update password"}
                      </button>
                    </div>
                    {passwordError ? (
                      <span className="cp-settings__hint cp-settings__hint--error">
                        {passwordError}
                      </span>
                    ) : null}
                  </>
                )}
              </>
            ) : (
              "••••••••"
            )}
          </div>
        </div>
      </div>
      <div className="cp-settings__section cp-grid">
        <div className="cp-settings__row">
          <span className="cp-settings__label">User ID</span>
          <div className="cp-settings__value">{userData.uid}</div>
        </div>
        <div className="cp-settings__row">
          <span className="cp-settings__label">Account Type</span>
          <div className="cp-settings__value">{accountTypeLabel}</div>
        </div>
        <div className="cp-settings__row">
          <span className="cp-settings__label">Member Since</span>
          <div className="cp-settings__value">{memberSinceLabel}</div>
        </div>
      </div>
    </div>
  );
}
